import React from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api, getToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { errorContext, correlationId } from '@/lib/errorContext';

/**
 * The last line of defence against a white screen.
 *
 * A React render error unmounts the whole tree, so before this existed a crash
 * showed the user nothing and told us nothing. This catches it, shows a page the
 * user can act on, and — for a signed-in staff member — files the bug itself,
 * with the page, the build and a reference the user can quote (ADR 0004 step 2).
 *
 * Deliberately NOT a third-party crash reporter: Sentry's HIPAA-eligible tier is
 * enterprise-only, and a crash payload from a page that was rendering a patient's
 * chart is PHI going to a vendor with no BAA (GAPS S21). This reports into our
 * own support desk instead.
 *
 * Patient pages have no session, and `POST /help/feedback` requires one, so a
 * patient crash gets the recovery screen and the reference but files no report.
 */
class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, reported: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    // Local first: whatever happens to the report, a developer sitting in front
    // of this still gets the real stack in the console.
    // eslint-disable-next-line no-console
    console.error('Render crash caught by ErrorBoundary', error, info?.componentStack);
    if (!getToken()) return;
    const context = errorContext(this.props.pathname, {
      kind: 'crash',
      error_name: error?.name || 'Error',
      error_message: String(error?.message || ''),
      component_stack: String(info?.componentStack || ''),
    });
    api.post('/help/feedback', {
      type: 'bug',
      severity: 'high',
      // The title carries no error text on purpose: a message we did not write
      // could quote a patient. The scrubbed message rides in `context` instead.
      title: `Crash: ${error?.name || 'Error'} on ${context.route}`,
      message: 'Automatic report from the app — the details are in the error context below.',
      context,
    }).then(() => this.setState({ reported: true })).catch(() => {});
  }

  componentDidUpdate(prev) {
    // Navigating away is the user's own "get me out of here", so let the next
    // page render. Resetting on the pathname (rather than keying the boundary)
    // keeps the shell's state intact on every navigation that did NOT crash.
    if (prev.pathname !== this.props.pathname && this.state.crashed) {
      this.setState({ crashed: false, reported: false });
    }
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full space-y-4 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-destructive" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">This page stopped working</h1>
            <p className="text-sm text-muted-foreground">
              Nothing you were working on was lost — signed documents and submitted
              forms are saved on our servers, not in this page.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {this.state.reported
              ? 'We have been sent a report automatically.'
              : 'Please tell us what you were doing when this happened.'}
            {' '}Reference <span className="font-mono">{correlationId()}</span>
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => window.location.reload()} data-testid="error-boundary-reload">
              <RefreshCw className="h-4 w-4 mr-2" /> Reload the page
            </Button>
            <Button variant="outline" onClick={() => { window.location.href = '/'; }} data-testid="error-boundary-home">
              Start again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export function ErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <Boundary pathname={pathname}>{children}</Boundary>;
}
